// i18n
import { translate } from 'infras/i18n';

import Event from 'domains/models/event';
import { LocationType } from 'domains/models/event_type/locationType';
import {
  formatToHour,
  formatToMinute,
  formatToYMD,
  DateTime,
  formatToDate,
  formatToTime,
  getDateTime,
  formatToDateTime,
} from 'infras/datetime';
import EventContact from 'domains/models/event_contact';
import {
  EventContactItem,
  EventContactListItem,
} from 'presentations/api/schema/event_contact';
import {
  EventListItem,
  SharedEventDetailItem,
} from 'presentations/api/schema/event';
import { SlackChannelListItem } from 'presentations/api/schema/slack_channel';
import { VideoServiceType } from 'domains/models/calendar/videoServiceType';
import { Currency } from 'domains/models/service_price/currency';
import { EventTypeServiceListItem } from 'presentations/api/schema/event_type_service';
import EventPaymentLineItem from 'domains/models/event_payment_line_item';
import { ServiceDetailListItem } from 'presentations/api/schema/service';
import { HostOperationType } from 'domains/models/event_type/hostOperationType';
import { decode } from 'html-entities';
import { CalendarEventListItem } from 'presentations/api/schema/calendar_event';
import { RestrictPeriod } from 'domains/models/service_restrict/restrictPeriod';
import { Status } from 'domains/models/event/status';
import EventCapacity from 'domains/models/event_capacity';
import EventTypeNotification from 'domains/models/event_type_notification';

export const formatDefaultTeamName = (userName: string | null) => {
  let teamName = translate('default team no name');
  if (userName) {
    teamName = translate('default team name', { name: userName });
  }
  return teamName;
};

export const formatSlackChannelName = (channel: SlackChannelListItem) => {
  const prefix = channel.isPrivate ? '🔒' : '#';
  return `${prefix} ${channel.name}`;
};

export const formatLocationName = (locationType: LocationType) => {
  switch (locationType) {
    case LocationType.InPerson:
      return translate('model.Account.providerId.InPerson');
    case LocationType.Zoom:
      return translate('model.Account.providerId.Zoom');
    case LocationType.GoogleMeet:
      return translate('model.Account.providerId.Google');
    case LocationType.Phone:
      return translate('model.Account.providerId.Phone');
    case LocationType.Teams:
      return translate('model.Account.providerId.Teams');
    case LocationType.AskInvitee:
    case LocationType.Custom:
    default:
      break;
  }
  return '';
};
export const formatLocationImage = (locationType: LocationType) => {
  switch (locationType) {
    case LocationType.Zoom:
      return 'pages/integrations/zoom.svg';
    case LocationType.GoogleMeet:
      return 'pages/integrations/googlemeet.svg';
    case LocationType.Teams:
      return 'pages/integrations/teams.svg';
    default:
      break;
  }
  return null;
};
export function formatVideoServiceTypeName(
  videoServiceType: VideoServiceType,
): string | null {
  switch (videoServiceType) {
    case VideoServiceType.Zoom:
      return translate('model.Account.providerId.Zoom');
    case VideoServiceType.GoogleMeet:
      return translate('model.Account.providerId.Google');
    case VideoServiceType.Jicoo:
      return 'Jicoo';
    case VideoServiceType.MicrosoftTeams:
      return 'Teams';
    default:
      break;
  }
  return null;
}

export const formatLocationNameByEvent = (
  event: Event | SharedEventDetailItem | EventListItem | CalendarEventListItem,
) => {
  const names = [];
  switch (event.locationId) {
    case LocationType.InPerson:
    case LocationType.Zoom:
    case LocationType.GoogleMeet:
    case LocationType.Phone:
    case LocationType.Teams:
      names.push(formatLocationName(event.locationId));
      break;
    case LocationType.AskInvitee:
      break;
    case LocationType.Custom:
      names.push(event.customLocation);
      break;
    default:
      break;
  }
  const locationName = names.join(translate('comma'));
  return locationName;
};

export const formatDateTimeRange = (
  startedAt: string | Date,
  endedAt: string | Date,
  timeZone: string,
  isAllday?: boolean,
) => {
  let startedAtLuxon = getDateTime(startedAt);
  let endedAtLuxon = getDateTime(endedAt);
  const allDay =
    isAllday !== undefined
      ? isAllday
      : startedAtLuxon.hour === 0 &&
        startedAtLuxon.minute === 0 &&
        endedAtLuxon.hour === 0 &&
        endedAtLuxon.minute === 0;
  if (!timeZone && !allDay) {
    // timeZoneがない&終日予定以外の場合、現在のオフセット分を適用
    startedAtLuxon = startedAtLuxon.minus({
      minutes: new Date().getTimezoneOffset(),
    });
    endedAtLuxon = endedAtLuxon.minus({
      minutes: new Date().getTimezoneOffset(),
    });
  }
  const startDate = formatToDate(startedAtLuxon.toJSDate(), timeZone);
  const startTime = formatToTime(startedAtLuxon.toJSDate(), timeZone);
  const endDate = formatToDate(
    endedAtLuxon.minus({ days: allDay ? 1 : 0 }).toJSDate(),
    timeZone,
  );
  const endTime = formatToTime(endedAtLuxon.toJSDate(), timeZone);

  let text = '';
  if (allDay) {
    text = startDate === endDate ? startDate : `${startDate} - ${endDate}`;
  } else if (startDate === endDate) {
    text = `${startDate} ${startTime} - ${endTime}`;
  } else {
    text = `${startDate} ${startTime} - ${endDate} ${endTime}`;
  }
  return `${text}`;
};

export const formatEventName = (
  event: Event,
  eventContact: EventContact | EventContactItem,
) => {
  const contactName = formatContactName(eventContact);
  let eventName = event.name;
  if (contactName) {
    eventName += ` w/ ${contactName}`;
  }
  return eventName;
};

export const addNameSuffix = (name: string) => {
  return translate('mailer.general.name with suffix', { name });
};

export const formatContactName = (
  eventContact: EventContact | EventContactListItem,
) => {
  const names = [eventContact.name];
  if (eventContact.company) {
    names.unshift(eventContact.company);
  }
  const contactName = names.join(' | ');
  return contactName;
};

export const formatGuestEventBookingMailSubject = (
  event: Event,
  hostName: string,
  isUpdates: boolean,
) => {
  let subject = translate('mailer.GuestEventReserve.subject', {
    eventName: event.name,
    hostName: hostName,
    company: '',
  });

  if (event.status === Status.Request) {
    subject = translate('mailer.GuestEventRequest.subject');
  }
  if (isUpdates) {
    subject = translate('mailer.GuestEventUpdate.subject', {
      hostName,
      eventName: event.name,
    });
  }

  return subject;
};

export const formatHostEventBookingMailSubject = (
  event: Event,
  contact: EventContact | EventContactListItem,
  isUpdates: boolean,
) => {
  let subject = translate('mailer.HostEventReserve.subject', {
    eventName: event.name,
    guestName: formatContactName(contact),
  });

  if (event.status === Status.Request) {
    subject = translate('mailer.HostEventRequest.subject');
  }
  if (isUpdates) {
    subject = translate('mailer.HostEventUpdate.subject', {
      eventName: event.name,
      guestName: formatContactName(contact),
    });
  }
  return subject;
};

export const formatHostName = (
  hostOperationType: HostOperationType,
  name: string,
  teamName: string | null,
) => {
  switch (hostOperationType) {
    case HostOperationType.Team:
      return teamName || name;
    case HostOperationType.Indivisual:
      return name;
    default:
      break;
  }
  const names = [name];
  if (teamName) {
    names.unshift(teamName);
  }
  return names.join(' | ');
};

export const stripHtmlTag = (html: string | null) => {
  if (!html) return html;
  return html.replace(/(<([^>]+)>)/gi, '');
};

export const formatHeadDescription = (description: string | null) => {
  let d = stripHtmlTag(decode(description));
  if (d) {
    const maxLength = 128;
    d = d.replace(/\n/gi, '').trim().substring(0, maxLength);
    if (d.length >= maxLength) {
      d = `${d}...`;
    }
  }
  return d;
};

export const formatReserveScheduleEventName = (name: string) => {
  return translate('poll.reserveScheduleEventName', {
    eventName: name,
  });
};

export const formatCalendarTitle = ({
  event,
  eventContact,
  hostName,
  eventTypeNotification,
}: {
  event: Event;
  eventContact: EventContact;
  hostName: string;
  eventTypeNotification: EventTypeNotification;
}): string => {
  let calendarTitle = '';

  if (eventTypeNotification.calendarInviteTitle) {
    const startedAt = formatToDateTime(
      getDateTime(event.startedAt).setZone(event.timeZone),
    );
    const endedAt = formatToDateTime(
      getDateTime(event.endedAt).setZone(event.timeZone),
    );
    return formatEventDynamicTemplateText(
      eventTypeNotification.calendarInviteTitle,
      {
        eventName: event.name,
        contactName: eventContact?.name || '',
        startedAtText: startedAt,
        endedAtText: endedAt,
        timeZone: event.timeZone,
        hostName: hostName,
      },
    );
  }

  if (event.isSeminar) {
    calendarTitle = translate('mailer.CalendarInvite.eventTitle', {
      eventName: event.name,
    });
  } else {
    if (eventContact.userId) {
      calendarTitle = translate('mailer.CalendarInvite.titleWithHost', {
        eventName: event.name,
        hostName,
        contactName: formatContactName(eventContact),
      });
    } else {
      calendarTitle = translate('mailer.CalendarInvite.title', {
        eventName: event.name,
        contactName: formatContactName(eventContact),
      });
    }
  }
  return calendarTitle;
};

export const formatGuestCalendarTitle = (
  hostName: string,
  eventName: string,
): string => {
  return `${hostName} (${eventName})`;
};

export const formatWorkflowMessage = (props: {
  eventName: string;
  eventStartedAt: string;
  eventTimeZone: string;
  userSettingTimeZone?: string;
  contactName: string;
  hostName: string;
  customMessage: string | null;
  isTextRemind?: boolean;
}): string => {
  const {
    eventName,
    eventStartedAt,
    eventTimeZone,
    userSettingTimeZone,
    contactName,
    hostName,
    customMessage,
  } = props;

  if (!customMessage) {
    return '';
  }

  let message = customMessage;
  if (eventTimeZone === userSettingTimeZone) {
    message = message.replaceAll('{{time_zone}}', '');
  }

  message = message
    .replaceAll('{{event_name}}', eventName)
    .replaceAll('{{contact_name}}', contactName)
    .replaceAll('{{event_date}}', formatToDate(eventStartedAt, eventTimeZone))
    .replaceAll(
      '{{event_started_at}}',
      formatToTime(eventStartedAt, eventTimeZone),
    )
    .replaceAll('{{time_zone}}', `(${eventTimeZone})`)
    .replaceAll('{{host_name}}', hostName)
    .trim();

  if (props.isTextRemind) {
    // テキストの場合はHTMLに埋め込まれている文字列を置換し、HTMLタグ文字列を削除する
    message = message
      .replaceAll('>Event Name<', `>${eventName}<`)
      .replaceAll('>Contact Name<', `>${contactName}<`)
      .replaceAll(
        '>Event Date<',
        `>${formatToDate(eventStartedAt, eventTimeZone)}<`,
      )
      .replaceAll(
        '>Event StartedAt<',
        `>${formatToTime(eventStartedAt, eventTimeZone)}<`,
      )
      .replaceAll('>TimeZone<', `>${eventTimeZone}<`)
      .replaceAll('>Host Name<', `>${hostName}<`)
      .replaceAll('>Line<', `><`)
      .trim();
    message = message.replaceAll(/(<([^>]+)>)/gi, '');
  }

  return message;
};

export const formatDynamicTemplate = (template: string): string => {
  return template.replaceAll('[[', '{{').replaceAll(']]', '}}');
};

export const formatPrice = (
  amount: number,
  currency: Currency | null | undefined,
) => {
  const amountText = !amount ? 0 : amount.toLocaleString();
  const currencyText =
    currency === Currency.USD
      ? translate('currency.usd')
      : translate('currency.jpy');
  return `${currencyText}${amountText}`;
};

export const formatTaxExcludedPrice = (
  service: ServiceDetailListItem | EventTypeServiceListItem,
) => {
  if (service.isPaid && service.price) {
    return formatPrice(service.price.unitAmount, service.price.currency);
  } else {
    return translate('service.free');
  }
};

export const formatServiceRestrictSummary = (
  service: EventTypeServiceListItem,
) => {
  let summary = '';
  if (
    service.restrictSetting?.isEnableUserMaxUsesCount &&
    service.restrictSetting?.userMaxUsesCount
  ) {
    const count = service.restrictSetting?.userMaxUsesCount;
    summary += translate('service.userUsesCount', { count });
  }
  if (service.restrictSetting?.isRestrictCount) {
    const restricts = [...service.restricts].sort((a, b) => {
      if (a.restrictPeriod > b.restrictPeriod) return 1;
      if (a.restrictPeriod < b.restrictPeriod) return -1;
      return 0;
    });
    if (restricts.length > 0) {
      summary += ' (';
      for (const restrict of restricts) {
        if (restrict.restrictPeriod === RestrictPeriod.Day) {
          summary += translate('service.dayRestrictCount', {
            count: restrict.value,
          });
        } else if (restrict.restrictPeriod === RestrictPeriod.Week) {
          summary += translate('service.weekRestrictCount', {
            count: restrict.value,
          });
        } else if (restrict.restrictPeriod === RestrictPeriod.Month) {
          summary += translate('service.monthRestrictCount', {
            count: restrict.value,
          });
        }

        if (restrict === restricts[restricts.length - 1]) {
          summary += translate('service.by');
        } else {
          summary += ' , ';
        }
      }
      summary += ')';
    }
  }
  return summary;
};

export const formatTaxIncludedPrice = (
  item: EventPaymentLineItem,
  currency: Currency | null | undefined,
) => {
  if (item.unitAmount === 0) {
    return translate('service.free');
  }
  const newItem = new EventPaymentLineItem(item);
  return formatPrice(
    EventPaymentLineItem.calcLineItemTaxIncludedAmount(newItem),
    currency,
  );
};

export const formatOrderItemDate = (
  startDateTime: DateTime,
  endDateTime: DateTime,
  timeZone: string,
) => {
  const ymd = formatToYMD(startDateTime, timeZone, { delimiter: '/' });
  const startHour = formatToHour(startDateTime, timeZone);
  const startMin = formatToMinute(startDateTime, timeZone);
  const endHour = formatToHour(endDateTime, timeZone);
  const endMin = formatToMinute(endDateTime, timeZone);

  return `${ymd} ${startHour}:${startMin} - ${endHour}:${endMin}`;
};

export const formatPhoneNumber = (
  countryCode: string | null,
  phoneNumber: string | null,
) => {
  if (countryCode) {
    return `+${countryCode}${phoneNumber}`;
  }
  return phoneNumber;
};

export const formatCustomContentToHtmlMail = (text: string) => {
  if (text === '<p><br></p>') {
    return '';
  }
  return text.replace(/\n/g, '<br>');
};

export const formatEventDynamicTemplateText = (
  templateText: string,
  {
    eventName,
    contactName,
    startedAtText,
    endedAtText,
    timeZone,
    hostName,
  }: {
    eventName: string;
    contactName: string;
    startedAtText: string;
    endedAtText: string;
    timeZone: string;
    hostName: string;
  },
) => {
  return (
    templateText
      .replaceAll('{{event_name}}', eventName)
      .replaceAll('{{contact_name}}', contactName)
      .replaceAll('{{event_date}}', `${startedAtText} - ${endedAtText}`)
      .replaceAll('{{event_started_at}}', startedAtText)
      .replaceAll('{{time_zone}}', timeZone)
      .replaceAll('{{host_name}}', hostName)
      .replaceAll('>Event Name<', `>${eventName}<`)
      .replaceAll('>Contact Name<', `>${contactName}<`)
      .replaceAll('>Event Date<', `>${startedAtText} - ${endedAtText}<`)
      .replaceAll('>Event StartedAt<', `>${startedAtText}<`)
      .replaceAll('>TimeZone<', `>${timeZone}<`)
      .replaceAll('>Host Name<', `>${hostName}<`)
      .replaceAll(
        '>Line<',
        `><hr style="border-top: 1px solid #eeeeee;margin:16px 0;"><`,
      )
      .trim() ?? ''
  );
};
